import { useState, useCallback, useEffect, useRef } from 'react';
import { usePage } from '@inertiajs/react';
import { DiscountCodeInput } from './DiscountCodeInput';
import { formatPrice } from '../../utils/formatPrice';
import type { CartSubscriptionInfo, DiscountInfo } from '../../types/cart';

interface DiscountPreviewData {
  code: string;
  discount: number;
  discountPercentage: number;
  discountableItems: string[];
  discountInfo: DiscountInfo;
}

interface CartSummaryMobileProps {
  subtotal: number;
  discountCode: string | null;
  discountAmount: number;
  discountError: string | null;
  discountInfo: DiscountInfo | null;
  deliveryPrice: number;
  total: number;
  allowsDiscountCode: boolean;
  isLoading: boolean;
  onApplyDiscount: (code: string) => Promise<void>;
  onPreviewDiscount: (code: string) => Promise<void>;
  onConfirmDiscount: () => Promise<void>;
  onCancelPreview: () => void;
  previewData: DiscountPreviewData | null;
  onClearDiscount: () => Promise<void>;
  checkoutUrl: string;
  canCheckout: boolean;
  subscription?: CartSubscriptionInfo | null;
  replacementCreditDiscount?: number;
}

export function CartSummaryMobile({
  subtotal,
  discountCode,
  discountAmount,
  discountError,
  discountInfo,
  deliveryPrice,
  total,
  allowsDiscountCode,
  isLoading,
  onApplyDiscount,
  onPreviewDiscount,
  onConfirmDiscount,
  onCancelPreview,
  previewData,
  onClearDiscount,
  checkoutUrl,
  canCheckout,
  subscription,
  replacementCreditDiscount,
}: CartSummaryMobileProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleExpand = useCallback(() => {
    setIsExpanded(true);
  }, []);

  const handleCollapse = useCallback(() => {
    setIsExpanded(false);
  }, []);

  return (
    <>
      {/* Minimized bar - always visible */}
      <div className="fixed bottom-0 left-0 right-0 z-[100] bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.1)]">
        {/* Total row with expand button */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div className="flex items-center gap-3">
            <span className="text-[15px] font-semibold text-gray-800">Total</span>
            <span className="text-lg font-bold text-gray-900">{formatPrice(total)}</span>
          </div>
          <button
            type="button"
            onClick={handleExpand}
            className="flex items-center gap-1.5 text-sm text-green-600 font-medium"
          >
            Ver detalle y descuentos
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="18 15 12 9 6 15" />
            </svg>
          </button>
        </div>

        {/* Checkout button - exact same positioning as expanded sheet */}
        <div className="px-4 pt-1 pb-3" style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }}>
          <a
            href={canCheckout ? checkoutUrl : '#'}
            className={`block w-full py-3.5 text-center no-underline text-base font-semibold rounded-lg transition-colors duration-150 ${
              canCheckout
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-300 text-white cursor-not-allowed'
            }`}
            onClick={(e) => {
              if (!canCheckout) e.preventDefault();
            }}
          >
            Continuar
          </a>
        </div>
      </div>

      {/* Expanded bottom sheet */}
      {isExpanded && (
        <CartSummaryBottomSheet
          subtotal={subtotal}
          discountCode={discountCode}
          discountAmount={discountAmount}
          discountError={discountError}
          discountInfo={discountInfo}
          deliveryPrice={deliveryPrice}
          total={total}
          allowsDiscountCode={allowsDiscountCode}
          isLoading={isLoading}
          onApplyDiscount={onApplyDiscount}
          onPreviewDiscount={onPreviewDiscount}
          onConfirmDiscount={onConfirmDiscount}
          onCancelPreview={onCancelPreview}
          previewData={previewData}
          onClearDiscount={onClearDiscount}
          checkoutUrl={checkoutUrl}
          canCheckout={canCheckout}
          subscription={subscription}
          replacementCreditDiscount={replacementCreditDiscount}
          onClose={handleCollapse}
        />
      )}
    </>
  );
}

function CartSummaryBottomSheet({
  subtotal,
  discountCode,
  discountAmount,
  discountError,
  discountInfo,
  deliveryPrice,
  total,
  allowsDiscountCode,
  isLoading,
  onApplyDiscount,
  onPreviewDiscount,
  onConfirmDiscount,
  onCancelPreview,
  previewData,
  onClearDiscount,
  checkoutUrl,
  canCheckout,
  subscription,
  replacementCreditDiscount,
  onClose,
}: CartSummaryMobileProps & { onClose: () => void }) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number | null>(null);
  const currentTranslateY = useRef(0);
  const rafPending = useRef(false);

  // Prevent body scroll while open + block touch events on backdrop
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    // Extra: prevent touchmove on the entire document while sheet is open
    const preventScroll = (e: TouchEvent) => {
      // Allow scroll inside the sheet's scrollable content
      const target = e.target as HTMLElement;
      if (target.closest('.sheet-scroll-content')) {
        return;
      }
      e.preventDefault();
    };

    document.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('touchmove', preventScroll);
    };
  }, []);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Drag logic using touch events (pointer events don't work reliably in iOS PWA)
  const handleDragStart = useCallback((clientY: number) => {
    dragStartY.current = clientY;
    currentTranslateY.current = 0;
  }, []);

  const handleDragMove = useCallback((clientY: number) => {
    if (dragStartY.current === null || !sheetRef.current) return;
    const dy = clientY - dragStartY.current;
    if (dy > 0) {
      currentTranslateY.current = dy;
      if (!rafPending.current) {
        rafPending.current = true;
        requestAnimationFrame(() => {
          if (sheetRef.current) {
            sheetRef.current.style.transform = `translateY(${currentTranslateY.current}px)`;
          }
          rafPending.current = false;
        });
      }
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragStartY.current === null || !sheetRef.current) return;
    dragStartY.current = null;

    const sheet = sheetRef.current;

    if (currentTranslateY.current > 80) {
      // Dismiss animation
      sheet.style.transition = 'transform 0.2s ease-in';
      sheet.style.transform = 'translateY(100%)';

      const handleTransitionEnd = () => {
        sheet.removeEventListener('transitionend', handleTransitionEnd);
        onClose();
      };
      sheet.addEventListener('transitionend', handleTransitionEnd);
    } else {
      // Snap back
      sheet.style.transition = 'transform 0.2s ease-out';
      sheet.style.transform = 'translateY(0)';

      const handleTransitionEnd = () => {
        sheet.removeEventListener('transitionend', handleTransitionEnd);
        sheet.style.transition = '';
      };
      sheet.addEventListener('transitionend', handleTransitionEnd);
    }
  }, [onClose]);

  // Touch event handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      handleDragStart(e.touches[0].clientY);
    }
  }, [handleDragStart]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      handleDragMove(e.touches[0].clientY);
    }
  }, [handleDragMove]);

  const handleTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Mouse event handlers (for desktop)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    handleDragStart(e.clientY);

    const onMouseMove = (ev: MouseEvent) => {
      handleDragMove(ev.clientY);
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      handleDragEnd();
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [handleDragStart, handleDragMove, handleDragEnd]);

  return (
    <div className="fixed inset-0 z-[200]" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className="absolute bottom-0 left-0 right-0 animate-slide-up rounded-t-2xl bg-white shadow-2xl flex flex-col"
        style={{ maxHeight: '85vh' }}
      >
        {/* Drag handle zone - entire header area */}
        <div
          className="shrink-0 touch-none cursor-grab active:cursor-grabbing"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          onMouseDown={handleMouseDown}
        >
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="h-1 w-10 rounded-full bg-gray-300" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-100 px-4 pb-3">
            <h3 className="text-base font-semibold text-gray-800">Resumen de compra</h3>
            <button
              type="button"
              onClick={onClose}
              onTouchEnd={(e) => e.stopPropagation()}
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Cerrar"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="sheet-scroll-content flex-1 overflow-y-auto px-4 pt-4">
          {/* Subscription banner */}
          {subscription && (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5 text-sm text-green-800">
              <span className="font-medium">{subscription.plan_name}</span>
              {' — '}
              {subscription.discount_percent}% de descuento
              {subscription.free_shipping && ' + envío gratis'}
            </div>
          )}

          {/* Discount code input */}
          {allowsDiscountCode && (
            <DiscountCodeInput
              initialCode={discountCode}
              discountAmount={discountAmount}
              discountError={discountError}
              discountInfo={discountInfo}
              isLoading={isLoading}
              onApplyDiscount={onApplyDiscount}
              onPreviewDiscount={onPreviewDiscount}
              onConfirmDiscount={onConfirmDiscount}
              onCancelPreview={onCancelPreview}
              previewData={previewData}
              onClearDiscount={onClearDiscount}
            />
          )}

          {/* Price breakdown */}
          <div className="bg-gray-50 rounded-lg p-3.5 mb-4">
            <div className="flex justify-between text-sm text-gray-500 py-1.5">
              <span>Subtotal</span>
              <span>{formatPrice(subtotal)}</span>
            </div>

            {subscription && subscription.discount_amount > 0 && (
              <div className="flex justify-between text-sm text-green-600 py-1.5">
                <span>{subscription.plan_name} ({subscription.discount_percent}%)</span>
                <span>-{formatPrice(subscription.discount_amount)}</span>
              </div>
            )}

            {discountAmount > 0 && discountCode && !discountInfo?.is_free_delivery && (
              <div className="flex justify-between text-sm text-green-600 py-1.5">
                <span>{discountInfo?.display_name || `Cupón (${discountCode.toUpperCase()})`}</span>
                <span>-{formatPrice(discountAmount)}</span>
              </div>
            )}

            {(replacementCreditDiscount ?? 0) > 0 && (
              <div className="flex justify-between text-sm text-green-600 py-1.5">
                <span>Credito por reposicion</span>
                <span>-{formatPrice(replacementCreditDiscount!)}</span>
              </div>
            )}

            <div className="flex justify-between text-sm py-1.5">
              <span className={discountInfo?.is_free_delivery ? 'text-green-600 font-medium' : 'text-gray-500'}>
                {discountInfo?.is_free_delivery ? '🚚 Envío gratis' : 'Envío'}
              </span>
              <span className={discountInfo?.is_free_delivery ? 'text-green-600' : 'text-gray-500'}>
                {discountInfo?.is_free_delivery ? (
                  <>
                    <span className="line-through text-gray-400 mr-2">{formatPrice(deliveryPrice)}</span>
                    <span className="font-medium">Gratis</span>
                  </>
                ) : deliveryPrice === 0 ? (
                  <span className={subscription?.free_shipping ? 'text-green-600 font-medium' : ''}>Gratis</span>
                ) : (
                  formatPrice(deliveryPrice)
                )}
              </span>
            </div>

            <div className="flex justify-between text-[17px] font-semibold text-gray-800 border-t border-gray-200 mt-2 pt-3">
              <span>Total</span>
              <span>{formatPrice(total)}</span>
            </div>
          </div>
        </div>

        {/* Fixed bottom button - exact same positioning as minimized bar */}
        <div className="shrink-0 px-4 pt-1 border-t border-gray-100" style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }}>
          <a
            href={canCheckout ? checkoutUrl : '#'}
            className={`block w-full py-3.5 text-center no-underline text-base font-semibold rounded-lg transition-colors duration-150 ${
              canCheckout
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-300 text-white cursor-not-allowed'
            }`}
            onClick={(e) => {
              if (!canCheckout) e.preventDefault();
            }}
          >
            Continuar
          </a>
          <MobileAuthHint />
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.25s ease-out;
        }
      `}</style>
    </div>
  );
}

function MobileAuthHint() {
  const { auth } = usePage<{ auth: { user: unknown | null } }>().props;
  if (auth?.user) return null;

  return (
    <p className="text-center text-[11px] text-gray-400 mt-2 mb-0">
      A continuación vas a iniciar sesión o crear una cuenta
    </p>
  );
}
